/**
 * The icon set behind `<Icon name="…" />` — the ONE place a glyph is defined.
 *
 * Self-hosted by construction: the geometry is inline path data on a 24×24 grid,
 * so there is no webfont, no icon CDN, no `@fortawesome` runtime dependency, and
 * a bundler tree-shakes whatever the app never names. That is the property
 * `design-system.md` actually asks for; a webfont icon set would violate the
 * no-CDN rule and ship every glyph to every user.
 *
 * Before this existed, icon geometry was pasted as per-file `const FOO_PATH`
 * string literals across the app and rendered with `fill="currentColor"` — which
 * silently mangled the stroke-style paths (a chevron `M15 4l-8 8 8 8` filled is
 * a solid wedge, not a chevron). Glyphs here declare how they must be painted.
 *
 * Most glyphs are STROKED (2px, round caps/joins) in the Feather/Lucide idiom;
 * solid shapes opt in with `fill: true`.
 */

/** One icon's geometry plus how it must be painted. */
export interface IconGlyph {
  /** SVG path `d` data on the glyph's `viewBox` grid. */
  readonly path: string;
  /** Paint as a solid shape rather than a 2px stroke. Defaults to stroked. */
  readonly fill?: boolean;
  /** Non-default viewBox. Defaults to `0 0 24 24`. */
  readonly viewBox?: string;
}

/**
 * Every named glyph. Adding an icon = adding a key (OCP); no caller changes, no
 * conditional. Names are semantic-neutral shape names (`chevron-left`), not
 * role names (`gallery-back`), so one glyph serves every surface.
 */
export const ICONS = {
  // --- Navigation -----------------------------------------------------------
  'chevron-left': { path: 'M15 18l-6-6 6-6' },
  'chevron-right': { path: 'M9 18l6-6-6-6' },
  'chevron-down': { path: 'M6 9l6 6 6-6' },
  'chevron-up': { path: 'M18 15l-6-6-6 6' },
  'arrow-left': { path: 'M19 12H5M12 19l-7-7 7-7' },
  'arrow-right': { path: 'M5 12h14M12 5l7 7-7 7' },
  menu: { path: 'M3 12h18M3 6h18M3 18h18' },
  close: { path: 'M18 6L6 18M6 6l12 12' },
  home: { path: 'M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2zM9 22V12h6v10' },
  'external-link': {
    path: 'M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6M15 3h6v6M10 14L21 3',
  },
  'more-horizontal': {
    path: 'M13.5 12a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM20.5 12a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM6.5 12a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z',
    fill: true,
  },

  // --- Actions --------------------------------------------------------------
  check: { path: 'M20 6L9 17l-5-5' },
  plus: { path: 'M12 5v14M5 12h14' },
  minus: { path: 'M5 12h14' },
  search: { path: 'M11 19a8 8 0 100-16 8 8 0 000 16zM21 21l-4.35-4.35' },
  download: { path: 'M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3' },
  upload: { path: 'M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12' },
  trash: {
    path: 'M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6M10 11v6M14 11v6',
  },
  send: { path: 'M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z' },
  refresh: {
    path: 'M23 4v6h-6M1 20v-6h6M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15',
  },
  copy: {
    path: 'M20 9H11a2 2 0 00-2 2v9a2 2 0 002 2h9a2 2 0 002-2v-9a2 2 0 00-2-2zM5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1',
  },
  'log-out': { path: 'M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9' },

  // --- Media ----------------------------------------------------------------
  play: { path: 'M8 5v14l11-7z', fill: true },
  pause: { path: 'M6 4h4v16H6zM14 4h4v16h-4z', fill: true },
  image: {
    path: 'M19 3H5a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2V5a2 2 0 00-2-2zM8.5 10a1.5 1.5 0 100-3 1.5 1.5 0 000 3zM21 15l-5-5L5 21',
  },
  video: {
    path: 'M23 7l-7 5 7 5V7zM14 5H3a2 2 0 00-2 2v10a2 2 0 002 2h11a2 2 0 002-2V7a2 2 0 00-2-2z',
  },
  camera: {
    path: 'M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2v11zM12 17a4 4 0 100-8 4 4 0 000 8z',
  },
  maximize: { path: 'M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7' },

  // --- Product / companion --------------------------------------------------
  heart: {
    path: 'M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z',
  },
  sparkles: {
    path: 'M12 3l1.9 4.6 4.6 1.9-4.6 1.9L12 16l-1.9-4.6L5.5 9.5l4.6-1.9L12 3zM19 15l.95 2.3L22 18l-2.05.7L19 21l-.95-2.3L16 18l2.05-.7L19 15z',
  },
  star: {
    path: 'M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z',
    fill: true,
  },
  'message-circle': {
    path: 'M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z',
  },
  user: { path: 'M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2M12 11a4 4 0 100-8 4 4 0 000 8z' },
  users: {
    path: 'M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2M9 11a4 4 0 100-8 4 4 0 000 8zM23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75',
  },
  gift: {
    path: 'M20 12v10H4V12M2 7h20v5H2zM12 22V7M12 7H7.5a2.5 2.5 0 010-5C11 2 12 7 12 7zM12 7h4.5a2.5 2.5 0 000-5C13 2 12 7 12 7z',
  },
  'credit-card': {
    path: 'M21 4H3a2 2 0 00-2 2v12a2 2 0 002 2h18a2 2 0 002-2V6a2 2 0 00-2-2zM1 10h22',
  },
  bell: { path: 'M18 8a6 6 0 00-12 0c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 01-3.46 0' },
  settings: {
    path: 'M12 15a3 3 0 100-6 3 3 0 000 6zM19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 11-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 11-4 0v-.09A1.65 1.65 0 008.6 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 11-2.83-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H2a2 2 0 110-4h.09A1.65 1.65 0 004.6 8.6a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 112.83-2.83l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V2a2 2 0 114 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 112.83 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H22a2 2 0 110 4h-.09a1.65 1.65 0 00-1.51 1z',
  },
  lock: {
    path: 'M19 11H5a2 2 0 00-2 2v7a2 2 0 002 2h14a2 2 0 002-2v-7a2 2 0 00-2-2zM7 11V7a5 5 0 0110 0v4',
  },
  eye: { path: 'M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8zM12 15a3 3 0 100-6 3 3 0 000 6z' },
  'eye-off': {
    path: 'M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19M14.12 14.12a3 3 0 11-4.24-4.24M1 1l22 22',
  },

  // --- Status ---------------------------------------------------------------
  'check-circle': { path: 'M22 11.08V12a10 10 0 11-5.93-9.14M22 4L12 14.01l-3-3' },
  'x-circle': { path: 'M12 22a10 10 0 100-20 10 10 0 000 20zM15 9l-6 6M9 9l6 6' },
  'alert-triangle': {
    path: 'M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0zM12 9v4M12 17h.01',
  },
  info: { path: 'M12 22a10 10 0 100-20 10 10 0 000 20zM12 16v-4M12 8h.01' },
  clock: { path: 'M12 22a10 10 0 100-20 10 10 0 000 20zM12 6v6l4 2' },
} as const satisfies Record<string, IconGlyph>;

/** Every glyph name `<Icon name="…" />` accepts. Typo-safe at compile time. */
export type IconName = keyof typeof ICONS;

/**
 * The glyph names as a runtime array — for icon-picker UIs and for the test
 * that asserts every glyph renders. Sorted so the order is stable.
 */
export const ICON_NAMES: readonly IconName[] = (Object.keys(ICONS) as IconName[]).sort();
